/**
 * S10 · Creator hub (design files 10 / 10w).
 *
 * Referral/creator dashboard. The only real data source available is the
 * consumer's waitlist entry (referral_code, referral_count,
 * referral_earnings_cents) — there is no reels/leaderboard/payout service,
 * so those design sections render neutral empty states rather than sample
 * numbers or invented content.
 *
 * LEGAL GATES (L1): no scheme name/mark, no patent claim, no present-tense
 * issuance claim, no fabricated money figures.
 */
import { buildShareUrl } from "@/services/share";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import ContextSwitcher from "@/components/ContextSwitcher";
import { getUser } from "@/services/auth";
import { listCardRequests } from "@/services/card-requests";
import { getUserWaitlistEntry, type WaitlistEntryRow } from "@/services/waitlist";
import {
  Display,
  FunnelContainer,
  FunnelPage,
  Lede,
  SectionTitle,
  Wordmark,
} from "@/components/consumer/chrome";
import { useSiteContent } from "@/hooks/useSiteContent";
import { S10_SECTION, S10_DEFAULTS } from "@/content/screens/s10";
import { useReferralTerms, resolveRewardCopy } from "@/lib/referral-terms";
import { getMyReferralSummary } from "@/services/referrals";

export default function S10Creator() {
  const { t } = useSiteContent(S10_SECTION, S10_DEFAULTS);
  const [email, setEmail] = useState<string | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [waitlist, setWaitlist] = useState<WaitlistEntryRow | null>(null);
  const [copiedCode, setCopiedCode] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  const [loading, setLoading] = useState(true);
  const referralTerms = useReferralTerms();
  const [discountsEarned, setDiscountsEarned] = useState<number | null>(null);
  useEffect(() => {
    let live = true;
    getMyReferralSummary().then(({ data }) => {
      if (live && data) setDiscountsEarned((data.discounts_unspent ?? 0) + (data.discounts_used ?? 0));
    });
    return () => {
      live = false;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data: auth } = await getUser();
      if (cancelled) return;
      if (!auth?.user) {
        setLoading(false);
        return;
      }
      setEmail(auth.user.email ?? null);
      setUserId(auth.user.id);

      const { data: rows } = await listCardRequests();
      if (cancelled) return;
      const mine = ((rows as any[]) || []).filter((r) => r.user_id === auth.user.id);
      const tenantId = mine[0]?.tenant_id;
      if (tenantId) {
        const { data: entry } = await getUserWaitlistEntry(tenantId, auth.user.id);
        if (!cancelled) setWaitlist(entry ?? null);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const shareLink = waitlist?.referral_code ? buildShareUrl(waitlist.referral_code) : null;

  async function copyCode() {
    if (!waitlist?.referral_code) return;
    try {
      await navigator.clipboard.writeText(waitlist.referral_code);
      setCopiedCode(true);
      setTimeout(() => setCopiedCode(false), 1500);
    } catch {
      /* clipboard unavailable */
    }
  }

  async function copyLink() {
    if (!shareLink) return;
    try {
      await navigator.clipboard.writeText(shareLink);
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 1500);
    } catch {
      /* clipboard unavailable */
    }
  }

  /* L15 · a referral reward is a COUNT of fee discounts resolved from config —
     never a cash balance. Copy figures come from program_referral_settings. */
  const codeTerms = resolveRewardCopy(t("s10.code.terms"), referralTerms);
  const referralCount = waitlist?.referral_count ?? 0;

  return (
    <FunnelPage testId="s10-creator" pb="pb-16">
      <div className="bg-ink">
        <FunnelContainer size="creator" className="flex items-center gap-3 py-3.5 pt-3.5 md:py-4">
          <Wordmark tone="paper" to="/" />
          <span className="flex-none rounded-full bg-volt/[0.16] px-[9px] py-1 text-[11px] font-semibold uppercase tracking-[0.1em] text-volt">
            {t("s10.header.badge")}
          </span>
          <span className="flex-1" />
          {email ? <span className="hidden text-[14px] font-medium text-paper/85 sm:inline">{email}</span> : null}
          {/* ONE identity control — contexts + log out, same as landing/S9. */}
          <ContextSwitcher variant="avatar" />
        </FunnelContainer>
      </div>

      <FunnelContainer size="creator" className="pt-[26px] md:pt-[34px]">
        <Display className="mb-2">{t("s10.header.title")}</Display>
        <Lede className="mb-6 max-w-[46ch]">
          {t("s10.header.subtitle")}
        </Lede>

        {loading ? (
          <p className="text-[15px] text-foreground/60" data-testid="s10-loading">
            {t("s10.body.loading")}
          </p>
        ) : !userId ? (
          <p className="text-[15px] text-foreground/60" data-testid="s10-signed-out">
            {t("s10.body.signedOut")}{" "}
            <Link to="/create" className="text-azure hover:text-tangerine">
              {t("s10.body.signIn")}
            </Link>{" "}
            {t("s10.body.signedOutSuffix")}
          </p>
        ) : (
          <>
            {/* Your code */}
            <div className="mb-[26px] rounded-[20px] bg-ink p-5 text-paper">
              <span className="text-[12px] font-semibold uppercase tracking-[0.1em] text-paper/60">{t("s10.code.sectionLabel")}</span>
              {waitlist?.referral_code ? (
                <>
                  <div className="mt-2 flex flex-wrap items-center gap-3">
                    <span className="font-brand text-[30px] font-semibold tracking-[-0.01em] text-volt" data-testid="s10-referral-code">
                      {waitlist.referral_code}
                    </span>
                    <span className="flex-1" />
                    <button
                      type="button"
                      onClick={copyCode}
                      data-testid="s10-copy-code"
                      className="flex min-h-11 cursor-pointer items-center rounded-full border border-paper/30 bg-transparent px-[18px] py-2.5 text-[14px] font-semibold text-paper transition-colors hover:border-paper"
                    >
                      {copiedCode ? t("s10.code.copiedCode") : t("s10.code.copyCode")}
                    </button>
                    <button
                      type="button"
                      onClick={copyLink}
                      data-testid="s10-copy-link"
                      className="flex min-h-11 cursor-pointer items-center rounded-full border-0 bg-volt px-[18px] py-2.5 text-[14px] font-semibold text-ink transition-colors hover:bg-paper"
                    >
                      {copiedLink ? t("s10.code.copiedLink") : t("s10.code.copyLink")}
                    </button>
                  </div>
                  {codeTerms ? (
                    <p className="m-0 mt-3.5 text-[14px] leading-[1.5] text-paper">{codeTerms}</p>
                  ) : null}
                </>
              ) : (
                <p className="m-0 mt-2 text-[15px] leading-[1.5] text-paper/80" data-testid="s10-no-code">
                  {t("s10.code.noCode")}
                </p>
              )}
            </div>

            {/* Approved reels — no content service exists yet, so no reels are invented */}
            <div className="mb-1.5 flex items-center gap-2.5">
              <SectionTitle>{t("s10.reels.sectionTitle")}</SectionTitle>
            </div>
            <div className="mb-[26px] rounded-[20px] border border-foreground/10 bg-white p-5 text-[15px] leading-[1.5] text-foreground/65" data-testid="s10-reels-empty">
              {t("s10.reels.empty")}
            </div>

            <div className="mb-[26px] flex items-start gap-3 rounded-2xl border border-foreground/10 bg-white px-4 py-[15px]">
              <svg viewBox="0 0 20 20" className="mt-px h-[19px] w-[19px] flex-none">
                <circle cx="10" cy="10" r="8.4" fill="none" stroke="#566178" strokeWidth="1.7" />
                <path d="M10 9v5" stroke="#566178" strokeWidth="1.8" strokeLinecap="round" />
                <circle cx="10" cy="6.2" r="1.1" fill="#566178" />
              </svg>
              <span className="text-[14px] leading-[1.55] text-foreground/[0.78]">
                {t("s10.info.claimsDisclosure")}
              </span>
            </div>

            {/* Performance */}
            <SectionTitle className="mb-3">{t("s10.performance.sectionTitle")}</SectionTitle>
            <div className="mb-3 grid grid-cols-2 gap-2.5">
              <div className="flex flex-col gap-1 rounded-2xl border border-foreground/10 bg-white p-[18px]">
                <span className="font-brand text-[32px] font-semibold leading-none tracking-[-0.02em]" data-testid="s10-referral-count">
                  {referralCount}
                </span>
                <span className="text-[13px] font-medium text-slate-muted">{t("s10.performance.cardsApproved")}</span>
              </div>
              {discountsEarned !== null ? (
                <div className="flex flex-col gap-1 rounded-2xl bg-volt p-[18px]">
                  <span className="font-brand text-[32px] font-semibold leading-none tracking-[-0.02em]" data-testid="s10-referral-discounts-earned">
                    {discountsEarned}
                  </span>
                  <span className="text-[13px] font-medium text-ink/80">{t("s10.performance.earnedLabel")}</span>
                </div>
              ) : null}
            </div>

            <p className="mt-[22px] text-center text-[12.5px] leading-[1.55] text-foreground/70">
              {t("s10.footer.disclaimer")}
            </p>
          </>
        )}
      </FunnelContainer>
    </FunnelPage>
  );
}
